import { useState } from "react";
import type { Doc, Id } from "@constructionautomation/backend/convex/_generated/dataModel";

import { useDocumentViewport } from "@/features/document-viewport/context";
import { DEFAULT_TEXT_FONT_SIZE } from "@/hooks/useMeasurementTextLock";
import {
  getMeasurementLabelCardStyle,
  getMeasurementLabelScale,
  getScreenPxPerPagePoint,
  MEASUREMENT_LABEL_CARD_CLASS_NAME,
  MeasurementLabelCardContent,
} from "@/viewport-tools/measurement/overlay/measurement-label-card";
import { pageWidthToScreenLength } from "@/viewport-tools/measurement/overlay/interactions/transform-interactions";
import { useCanEdit, useOnEditAccessLost } from "@/lib/capabilities";

export type DraftPoint = { x: number; y: number };

const POINT_MARKER_PAGE_RADIUS = 0.006;
const MIN_POINT_MARKER_SCREEN_RADIUS = 3;
const MAX_POINT_MARKER_SCREEN_RADIUS = 10;
const POINT_HIT_TARGET_SCREEN_RADIUS = 12;

export function getFieldPointMarkerRadius(
  pageToScreenPoint: (point: DraftPoint) => DraftPoint
): number {
  const pageRelativeRadius = pageWidthToScreenLength(
    POINT_MARKER_PAGE_RADIUS,
    pageToScreenPoint
  );
  return Math.min(
    MAX_POINT_MARKER_SCREEN_RADIUS,
    Math.max(MIN_POINT_MARKER_SCREEN_RADIUS, pageRelativeRadius)
  );
}

export interface FieldPointOverlayProps {
  items: Doc<"fieldPilotItems">[];
  statusById: Map<string, { name: string; colorHex: string }>;
  colorByItemId: Map<string, string>;
  labelByItemId: Map<string, string>;
  annotationByItemId: Map<string, string>;
  selectedItemIds: Set<string>;
  showLabels?: boolean;
  showValues?: boolean;
  textLocked?: boolean;
  textFontSize?: number;
  onSelectItem: (event: React.PointerEvent, itemId: Id<"fieldPilotItems">) => void;
  onUpdatePoint: (item: Doc<"fieldPilotItems">, point: DraftPoint) => void;
}

export function FieldPointOverlay({
  items,
  statusById,
  colorByItemId,
  labelByItemId,
  annotationByItemId,
  selectedItemIds,
  showLabels = true,
  showValues = true,
  textLocked = false,
  textFontSize = DEFAULT_TEXT_FONT_SIZE,
  onSelectItem,
  onUpdatePoint,
}: FieldPointOverlayProps) {
  const viewport = useDocumentViewport();
  // Moving a point is a project write; reviewers can select points but not drag them.
  const canEdit = useCanEdit();
  const { workspaceSize, transforms, camera } = viewport;
  const [draggingPoint, setDraggingPoint] = useState<{
    item: Doc<"fieldPilotItems">;
    pointerId: number;
  } | null>(null);
  const [previewPoint, setPreviewPoint] = useState<DraftPoint | null>(null);
  // A drag in flight when the member is demoted is dropped rather than saved.
  useOnEditAccessLost(() => {
    setDraggingPoint(null);
    setPreviewPoint(null);
  });

  const getPagePoint = (event: React.PointerEvent, svg: SVGSVGElement | null) => {
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    return transforms.screenToPagePoint({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    });
  };

  const handlePointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
    if (!draggingPoint) return;
    const point = getPagePoint(event, event.currentTarget);
    if (!point) return;
    setPreviewPoint(point);
  };

  const pointItems = items.filter((item) => item.geometry.type === "point");
  const { baseFontSize: labelFontSize, scale: labelScale } = getMeasurementLabelScale(
    textFontSize,
    getScreenPxPerPagePoint(viewport),
    camera.zoom,
    textLocked
  );
  const markerRadius = getFieldPointMarkerRadius(transforms.pageToScreenPoint);

  return (
    <svg
      className="pointer-events-none absolute inset-0 z-30"
      width={workspaceSize.width}
      height={workspaceSize.height}
      aria-hidden="true"
      onPointerMove={handlePointerMove}
      onPointerUp={(event) => {
        if (draggingPoint?.pointerId === event.pointerId) {
          if (previewPoint && canEdit) {
            onUpdatePoint(draggingPoint.item, previewPoint);
          }
          event.currentTarget.releasePointerCapture(event.pointerId);
          setDraggingPoint(null);
          setPreviewPoint(null);
        }
      }}
      onPointerCancel={(event) => {
        if (draggingPoint?.pointerId === event.pointerId) {
          event.currentTarget.releasePointerCapture(event.pointerId);
          setDraggingPoint(null);
          setPreviewPoint(null);
        }
      }}
    >
      {pointItems.map((item) => {
        if (item.geometry.type !== "point") return null;
        const itemPoint = item.geometry;
        const color =
          colorByItemId.get(String(item._id)) ??
          statusById.get(String(item.statusId))?.colorHex ??
          "#94A3B8";
        const label = labelByItemId.get(String(item._id)) ?? "Tag";
        const annotationLine = annotationByItemId.get(String(item._id));
        const isSelected = selectedItemIds.has(String(item._id));
        const geometryPoint =
          draggingPoint?.item._id === item._id && previewPoint ? previewPoint : itemPoint;
        const point = transforms.pageToScreenPoint({
          x: geometryPoint.x,
          y: geometryPoint.y,
        });
        const labelX = point.x + markerRadius + 5;
        const labelY = point.y - 18;
        return (
          <g key={item._id} className="pointer-events-auto">
            {isSelected ? (
              <circle
                cx={point.x}
                cy={point.y}
                r={markerRadius + 5}
                fill="none"
                stroke="var(--primary)"
                strokeDasharray="5 3"
                strokeWidth={2}
              />
            ) : null}
            <circle
              cx={point.x}
              cy={point.y}
              r={markerRadius}
              fill={color}
              stroke={color}
              strokeWidth={2}
              opacity={0.95}
              className="pointer-events-none"
            />
            <circle
              cx={point.x}
              cy={point.y}
              r={POINT_HIT_TARGET_SCREEN_RADIUS}
              fill="transparent"
              className={canEdit ? "cursor-grab" : "cursor-pointer"}
              onPointerDown={(event) => {
                event.stopPropagation();
                onSelectItem(event, item._id);
                // Shift-click is selection-only; avoid starting a drag that can
                // persist geometry from normal pointer jitter.
                if (event.shiftKey || !canEdit) {
                  return;
                }
                event.currentTarget.ownerSVGElement?.setPointerCapture(event.pointerId);
                setDraggingPoint({ item, pointerId: event.pointerId });
                // Keep preview null until move so a plain click does not persist
                // unchanged geometry / no-op undo entries.
              }}
            />
            {showLabels ? (
              <>
                <line
                  x1={point.x}
                  y1={point.y}
                  x2={labelX}
                  y2={labelY + labelFontSize}
                  stroke={color}
                  strokeWidth={1.25}
                  opacity={0.85}
                />
                <foreignObject
                  x={labelX}
                  y={labelY}
                  width={180}
                  height={48}
                  style={{ overflow: "visible" }}
                >
                  <div
                    className={`pointer-events-none inline-block origin-top-left select-none ${MEASUREMENT_LABEL_CARD_CLASS_NAME}`}
                    style={{
                      ...getMeasurementLabelCardStyle({
                        borderColor: color,
                        fontSize: labelFontSize,
                      }),
                      transform: `scale(${labelScale})`,
                    }}
                  >
                    <MeasurementLabelCardContent
                      label={label}
                      annotationLine={annotationLine}
                      valueLine={showValues ? "1 Count" : undefined}
                    />
                  </div>
                </foreignObject>
              </>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}
